"use client";

import { useLocale } from "@calcom/lib/hooks/useLocale";
import { Button } from "@calcom/ui/components/button";
import { Icon } from "@calcom/ui/components/icon";
import { showToast } from "@calcom/ui/components/toast";
import classNames from "@calcom/ui/classNames";
import { useCallback, useEffect, useState } from "react";

import {
  applyKnopkaBookingPageAppearance,
  parseBookingPageAppearance,
  type KnopkaBookingPageAppearance,
} from "../knopkaBookingPageAppearance";
import {
  defaultGlassSettings,
  resolveGlassLayers,
  type KnopkaGlassLayerOverrides,
  type KnopkaGlassPerformance,
  type KnopkaGlassPreset,
  type KnopkaGlassSettings,
} from "../knopkaGlassTokens";
import { KnopkaBookingPageBackgroundPreview } from "./KnopkaBookingPageBackground";

type Props = {
  username?: string | null;
  variant?: "inline" | "floating";
  onClose?: () => void;
  onSaved?: (appearance: KnopkaBookingPageAppearance) => void;
};

const PRESETS: KnopkaGlassPreset[] = ["subtle", "medium", "strong", "hero"];
const PERFORMANCE_MODES: KnopkaGlassPerformance[] = ["auto", "high", "balanced", "low"];

function layerValue(
  settings: KnopkaGlassSettings,
  key: keyof KnopkaGlassLayerOverrides
): number {
  const resolved = resolveGlassLayers(settings);
  const map: Record<keyof KnopkaGlassLayerOverrides, number> = {
    fillOpacity: resolved.fillOpacity,
    blur: resolved.blur,
    saturate: resolved.saturate,
    borderOpacity: resolved.borderOpacity,
    shadowStrength: resolved.shadowStrength,
    innerGlow: resolved.innerGlow,
    noise: resolved.noise,
    liquid: resolved.liquid,
  };
  return map[key];
}

export function KnopkaBookingPageAppearanceSettingsPanel({
  username,
  variant = "inline",
  onClose,
  onSaved,
}: Props) {
  const { t } = useLocale();
  const [enabled, setEnabled] = useState(false);
  const [bgUrl, setBgUrl] = useState("");
  const [overlay, setOverlay] = useState(55);
  const [customCss, setCustomCss] = useState("");
  const [customScripts, setCustomScripts] = useState("");
  const [glassSettings, setGlassSettings] = useState<KnopkaGlassSettings>(defaultGlassSettings());
  const [fineTuneOpen, setFineTuneOpen] = useState(false);
  const [loading, setLoading] = useState(Boolean(username));
  const [saving, setSaving] = useState(false);

  const previewAppearance = useCallback((): KnopkaBookingPageAppearance => {
    const backgroundUrl = enabled ? bgUrl.trim() : "";
    return {
      enabled,
      backgroundUrl,
      overlayOpacity: overlay,
      customCss,
      customScripts,
      glassSettings,
      active: Boolean(enabled && backgroundUrl),
    };
  }, [bgUrl, customCss, customScripts, enabled, glassSettings, overlay]);

  useEffect(() => {
    if (!username) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(
          `/api/knopka/page-appearance?username=${encodeURIComponent(username)}`
        );
        if (!response.ok) return;
        const data = await response.json();
        if (cancelled || !data?.ok) return;
        const next = parseBookingPageAppearance(data);
        setEnabled(next.enabled);
        setBgUrl(next.backgroundUrl);
        setOverlay(next.overlayOpacity);
        setCustomCss(next.customCss);
        setCustomScripts(next.customScripts);
        setGlassSettings(next.glassSettings);
      } catch {
        // Keep defaults when settings cannot be loaded.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [username]);

  useEffect(() => {
    if (!enabled || !bgUrl.trim()) return;
    const timer = window.setTimeout(() => {
      applyKnopkaBookingPageAppearance(previewAppearance());
    }, 120);
    return () => window.clearTimeout(timer);
  }, [enabled, bgUrl, overlay, customCss, customScripts, glassSettings, previewAppearance]);

  const updateGlassLayer = (key: keyof KnopkaGlassLayerOverrides, value: number) => {
    setGlassSettings((current) => ({
      ...current,
      layers: { ...current.layers, [key]: value },
    }));
  };

  const save = async (next: {
    enabled: boolean;
    backgroundUrl: string;
    overlayOpacity: number;
    customCss: string;
    customScripts: string;
    glassSettings: KnopkaGlassSettings;
  }) => {
    if (!username) return;
    setSaving(true);
    try {
      const response = await fetch("/api/knopka/page-appearance", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username,
          enabled: next.enabled,
          background_url: next.backgroundUrl,
          overlay_opacity: next.overlayOpacity,
          custom_css: next.customCss,
          custom_scripts: next.customScripts,
          glass_settings: next.glassSettings,
        }),
      });
      const data = await response.json();
      if (!response.ok || !data?.ok) {
        showToast(data?.error || t("knopka_page_appearance_save_failed"), "error");
        return;
      }
      const saved = parseBookingPageAppearance(data);
      setEnabled(saved.enabled);
      setBgUrl(saved.backgroundUrl);
      setOverlay(saved.overlayOpacity);
      setCustomCss(saved.customCss);
      setCustomScripts(saved.customScripts);
      setGlassSettings(saved.glassSettings);
      applyKnopkaBookingPageAppearance(saved);
      onSaved?.(saved);
      showToast(
        next.enabled ? t("knopka_appearance_saved") : t("knopka_page_appearance_reset_standard_done"),
        "success"
      );
      onClose?.();
    } catch {
      showToast(t("knopka_page_appearance_save_failed"), "error");
    } finally {
      setSaving(false);
    }
  };

  if (!username) {
    return (
      <section className="border-subtle bg-cal-muted mt-8 rounded-xl border p-4">
        <p className="text-subtle text-sm">{t("knopka_share_username_missing")}</p>
      </section>
    );
  }

  const noiseDisabled =
    glassSettings.preset !== "hero" &&
    glassSettings.performance !== "high" &&
    glassSettings.performance !== "auto";

  const content = (
    <>
      <div className={classNames("mb-3", variant === "floating" ? "flex items-start justify-between gap-3" : "")}>
        <div>
          <h2
            id="knopka-page-appearance-dialog-title"
            className="text-emphasis flex items-center gap-2 text-base font-semibold">
            <Icon name="layers" className="text-brand h-5 w-5" aria-hidden />
            {t("knopka_page_appearance_title")}
          </h2>
          <p className="text-subtle mt-1 text-sm leading-relaxed">{t("knopka_page_appearance_hint")}</p>
        </div>
        {variant === "floating" && onClose ? (
          <button
            type="button"
            className="text-subtle hover:text-emphasis rounded-md p-1"
            aria-label={t("close")}
            onClick={onClose}>
            <Icon name="x" className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      {loading ? (
        <p className="text-subtle text-sm">{t("loading")}</p>
      ) : (
        <>
          <label className="mb-4 flex cursor-pointer items-center justify-between gap-3">
            <span className="text-default text-sm font-medium">{t("knopka_appearance_enabled")}</span>
            <input
              type="checkbox"
              className="accent-brand h-5 w-5 shrink-0"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
          </label>

          {enabled ? (
            <>
              <label className="text-subtle mb-1 block text-xs font-semibold uppercase tracking-wide">
                {t("knopka_appearance_bg_url")}
              </label>
              <input
                type="url"
                value={bgUrl}
                onChange={(e) => setBgUrl(e.target.value)}
                placeholder="https://example.com/photo.jpg"
                className="border-subtle bg-default text-default focus:border-brand mb-3 w-full rounded-xl border px-3 py-2.5 text-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brand/30"
              />

              <KnopkaBookingPageBackgroundPreview appearance={{ backgroundUrl: bgUrl }} />

              <label className="text-subtle mb-1 block text-xs font-semibold uppercase tracking-wide">
                {t("knopka_appearance_overlay")} — {overlay}%
              </label>
              <input
                type="range"
                min={0}
                max={90}
                value={overlay}
                onChange={(e) => setOverlay(parseInt(e.target.value, 10) || 0)}
                className="mb-4 w-full accent-[var(--knopka-tg-accent,#6366f1)]"
              />

              <label className="text-subtle mb-1 block text-xs font-semibold uppercase tracking-wide">
                {t("knopka_glass_preset")}
              </label>
              <select
                value={glassSettings.preset}
                onChange={(e) =>
                  setGlassSettings((current) => ({
                    ...current,
                    preset: e.target.value as KnopkaGlassPreset,
                    layers: undefined,
                  }))
                }
                className="border-subtle bg-default text-default focus:border-brand mb-4 w-full rounded-xl border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30">
                {PRESETS.map((preset) => (
                  <option key={preset} value={preset}>
                    {t(`knopka_glass_preset_${preset}`)}
                  </option>
                ))}
              </select>

              <label className="text-subtle mb-1 block text-xs font-semibold uppercase tracking-wide">
                {t("knopka_glass_performance")}
              </label>
              <select
                value={glassSettings.performance}
                onChange={(e) =>
                  setGlassSettings((current) => ({
                    ...current,
                    performance: e.target.value as KnopkaGlassPerformance,
                  }))
                }
                className="border-subtle bg-default text-default focus:border-brand mb-4 w-full rounded-xl border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30">
                {PERFORMANCE_MODES.map((mode) => (
                  <option key={mode} value={mode}>
                    {t(`knopka_glass_performance_${mode}`)}
                  </option>
                ))}
              </select>

              <button
                type="button"
                className="text-default mb-3 flex w-full items-center justify-between rounded-lg border border-subtle px-3 py-2 text-sm font-medium"
                onClick={() => setFineTuneOpen((open) => !open)}
                aria-expanded={fineTuneOpen}>
                {t("knopka_glass_fine_tune")}
                <Icon name={fineTuneOpen ? "chevron-up" : "chevron-down"} className="h-4 w-4" />
              </button>

              {fineTuneOpen ? (
                <div className="border-subtle mb-4 space-y-3 rounded-xl border p-3">
                  {(
                    [
                      ["fillOpacity", 0, 100, 1, "knopka_glass_fill"],
                      ["blur", 0, 40, 1, "knopka_glass_blur"],
                      ["saturate", 1, 2, 0.05, "knopka_glass_saturate"],
                      ["borderOpacity", 0, 100, 1, "knopka_glass_border"],
                      ["shadowStrength", 0, 100, 1, "knopka_glass_shadow"],
                      ["innerGlow", 0, 100, 1, "knopka_glass_inner_glow"],
                      ["noise", 0, 100, 1, "knopka_glass_noise"],
                      ["liquid", 0, 100, 1, "knopka_glass_liquid"],
                    ] as const
                  ).map(([key, min, max, step, labelKey]) => {
                    const isNoise = key === "noise" || key === "liquid";
                    const disabled = isNoise && noiseDisabled;
                    const value = layerValue(glassSettings, key);
                    return (
                      <div key={key}>
                        <label className="text-subtle mb-1 block text-xs font-semibold uppercase tracking-wide">
                          {t(labelKey)} — {key === "saturate" ? value.toFixed(2) : value}
                          {disabled ? ` (${t("knopka_glass_noise_disabled")})` : ""}
                        </label>
                        <input
                          type="range"
                          min={min}
                          max={max}
                          step={step}
                          value={value}
                          disabled={disabled}
                          onChange={(e) => updateGlassLayer(key, parseFloat(e.target.value))}
                          className="w-full accent-[var(--knopka-tg-accent,#6366f1)] disabled:opacity-40"
                        />
                      </div>
                    );
                  })}
                </div>
              ) : null}

              <label className="text-subtle mb-1 block text-xs font-semibold uppercase tracking-wide">
                {t("knopka_page_appearance_css")}
              </label>
              <textarea
                value={customCss}
                onChange={(e) => setCustomCss(e.target.value)}
                rows={6}
                spellCheck={false}
                placeholder={t("knopka_page_appearance_css_placeholder")}
                className="border-subtle bg-default text-default focus:border-brand mb-3 w-full rounded-xl border px-3 py-2.5 font-mono text-xs leading-relaxed focus:outline-none focus:ring-2 focus:ring-brand/30"
              />
              <p className="text-subtle mb-4 text-xs leading-relaxed">{t("knopka_page_appearance_css_hint")}</p>

              <label className="text-subtle mb-1 block text-xs font-semibold uppercase tracking-wide">
                {t("knopka_page_appearance_scripts")}
              </label>
              <textarea
                value={customScripts}
                onChange={(e) => setCustomScripts(e.target.value)}
                rows={6}
                spellCheck={false}
                placeholder={t("knopka_page_appearance_scripts_placeholder")}
                className="border-subtle bg-default text-default focus:border-brand mb-3 w-full rounded-xl border px-3 py-2.5 font-mono text-xs leading-relaxed focus:outline-none focus:ring-2 focus:ring-brand/30"
              />
              <p className="text-subtle mb-4 text-xs leading-relaxed">{t("knopka_page_appearance_scripts_hint")}</p>
            </>
          ) : null}

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              color="primary"
              loading={saving}
              StartIcon="check"
              onClick={() => {
                if (enabled && !bgUrl.trim()) {
                  showToast(t("knopka_appearance_url_required"), "error");
                  return;
                }
                void save({
                  enabled,
                  backgroundUrl: enabled ? bgUrl.trim() : "",
                  overlayOpacity: overlay,
                  customCss,
                  customScripts,
                  glassSettings: enabled ? glassSettings : defaultGlassSettings(),
                });
              }}>
              {t("knopka_appearance_save")}
            </Button>
            <Button
              type="button"
              color="secondary"
              disabled={saving}
              StartIcon="rotate-ccw"
              onClick={() => {
                const resetGlass = defaultGlassSettings();
                setEnabled(false);
                setBgUrl("");
                setOverlay(55);
                setCustomCss("");
                setCustomScripts("");
                setGlassSettings(resetGlass);
                void save({
                  enabled: false,
                  backgroundUrl: "",
                  overlayOpacity: 55,
                  customCss: "",
                  customScripts: "",
                  glassSettings: resetGlass,
                });
              }}>
              {t("knopka_page_appearance_reset_standard")}
            </Button>
          </div>
        </>
      )}
    </>
  );

  if (variant === "floating") {
    return <div className="knopka-page-appearance-editor-content">{content}</div>;
  }

  return (
    <section className="border-subtle bg-cal-muted mt-8 rounded-xl border p-4">{content}</section>
  );
}
